import {
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'

import { UnmatchedUpload } from '__generated__/atlas-types'

import {
  PRODUCT_IMAGES_LIMIT,
  UPLOAD_PRODUCT_MAX_AMOUNT,
} from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.constants'
import { useGetDropzoneState } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.hooks'
import { ProductImage } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.types'
import useStore from 'features/Linesheets/Modals/ManageImagesModal/store/useManageImagesStore'

export const useProductImagesProps = () => {
  const isRemovingImages = useStore((state) => state.isRemovingImages)
  const isSavingImages = useStore((state) => state.isSavingImages)
  const isUploadingImages = useStore((state) => state.isUploadingImages)
  const productImages = useStore((state) => state.productImages)
  const setProductImages = useStore((state) => state.setProductImages)
  const selectedImages = useStore((state) => state.selectedImages)
  const setSelectedImages = useStore((state) => state.setSelectedImages)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const canUploadImage =
    productImages.length < PRODUCT_IMAGES_LIMIT && !selectedImages.size

  const canAssignImages =
    productImages.length < PRODUCT_IMAGES_LIMIT && selectedImages.size

  const areActionsDisabled =
    isRemovingImages || isSavingImages || isUploadingImages

  const appendProductImages = (
    productImages: ProductImage[],
    imagesToAppend: UnmatchedUpload[],
  ): void => {
    const newProductImages = [...productImages]

    imagesToAppend.every((image) => {
      const idx = newProductImages.findIndex(
        (img) =>
          (img.unmatchedUploadId &&
            image.unmatchedUploadId &&
            img.unmatchedUploadId === image.unmatchedUploadId) ||
          img.id === image.id,
      )
      if (idx > -1) {
        return true
      }

      newProductImages.push({
        id: image.id as string,
        url: image.url as string,
        fileName: image.fileName,
        unmatchedUploadId: image.unmatchedUploadId,
      })

      return newProductImages.length < PRODUCT_IMAGES_LIMIT
    })

    setProductImages({ productImages: newProductImages })
  }

  const handleAssignImages = () => {
    if (!canAssignImages || areActionsDisabled) {
      return
    }

    appendProductImages(productImages, Array.from(selectedImages.values()))
    setSelectedImages({ selectedImages: new Map() })
  }

  const { getInputProps, getRootProps } = useGetDropzoneState(
    (img) => appendProductImages(productImages, [img]),
    UPLOAD_PRODUCT_MAX_AMOUNT,
    !canUploadImage || areActionsDisabled,
  )

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = productImages.findIndex(
      (img) =>
        ((img.unmatchedUploadId as string) || (img.id as string)) === active.id,
    )
    if (oldIndex < 0) {
      return
    }
    const newIndex = productImages.findIndex(
      (img) =>
        ((img.unmatchedUploadId as string) || (img.id as string)) === over.id,
    )
    if (newIndex < 0) {
      return
    }

    const newProductImages = arrayMove(productImages, oldIndex, newIndex)
    setProductImages({ productImages: newProductImages })
  }

  const handleInputEvent = (e: React.DragEvent | React.MouseEvent): void => {
    if (areActionsDisabled) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  return {
    areActionsDisabled,
    canAssignImages,
    canUploadImage,
    productImages,
    sensors,
    selectedImages,
    handleAssignImages,
    handleInputEvent,
    getInputProps,
    getRootProps,
    onDragEnd,
  }
}
